import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { formatCurrency } from '../../utils/currency';
import CommentsSection from '../../components/Comments/CommentsSection';
import FavoriteButton from '../../components/Favorites/FavoriteButton';
import StartConversation from '../../components/Messaging/StartConversation';
import LikeButton from '../../components/Projects/LikeButton';
import ShareButton from '../../components/Projects/ShareButton';
import ReportProjectButton from '../../components/Projects/ReportProjectButton';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import BadgeEntrepriseSuivie from '../../components/Projects/BadgeEntrepriseSuivie';

const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactMessage, setContactMessage] = useState('');
  const [showStartConversation, setShowStartConversation] = useState(false);

  useEffect(() => {
    fetchProject();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchProject = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/projects/${id}`);
      setProject(response.data);
    } catch (error) {
      console.error('Erreur chargement projet:', error);
      toast.error('Erreur lors du chargement du projet');
      navigate('/projects');
    } finally {
      setLoading(false);
    }
  };


  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      toast.error('Vous devez être connecté pour contacter l\'auteur');
      return;
    }

    // Ici, vous pourriez implémenter l'envoi d'email ou de message
    toast.success('Message envoyé avec succès !');
    setShowContactForm(false);
    setContactMessage('');
  };

  const isAuthor = user && project && user.id === project.authorId;

  const handleDelete = async () => {
    if (!window.confirm(`Supprimer définitivement le projet « ${project.title} » ? Cette action est irréversible.`)) return;
    try {
      await api.delete(`/projects/${project.id}`);
      toast.success('Projet supprimé');
      navigate('/my-projects');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erreur lors de la suppression du projet');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
            <p className="mt-4 text-gray-600">Chargement du projet...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-background py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-text mb-4">Projet non trouvé</h1>
            <Link to="/projects" className="text-primary-500 hover:text-primary-600">
              Retour à la liste des projets
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav className="mb-8">
          <Link to="/projects" className="text-primary-500 hover:text-primary-600">
            ← Retour aux projets
          </Link>
        </nav>

        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Header du projet */}
          <div className="p-8 border-b border-gray-200">
            <div className="flex items-start justify-between mb-6">
              <div className="flex-1">
                <h1 className="text-3xl font-bold text-text mb-4">
                  {project.title}
                </h1>
                <div className="flex items-center space-x-4 mb-4">
                  <span className="px-3 py-1 bg-primary-100 text-primary-800 text-sm rounded-full">
                    {project.category}
                  </span>
                  <span className="px-3 py-1 bg-secondary-100 text-secondary-800 text-sm rounded-full">
                    {project.stage}
                  </span>
                  <span className="px-3 py-1 bg-gray-100 text-gray-800 text-sm rounded-full">
                    {project.location?.country || project.countryName || project.country}
                  </span>
                </div>
              </div>
              
              {isAuthor && (
                <div className="flex space-x-2">
                  <Link
                    to={`/projects/${project.id}/edit`}
                    className="px-4 py-2 bg-secondary-500 text-white rounded-lg hover:bg-secondary-600 transition-colors"
                  >
                    Modifier
                  </Link>
                  <button
                    type="button"
                    onClick={handleDelete}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center"
                  >
                    <TrashIcon className="h-4 w-4 mr-1" />
                    Supprimer
                  </button>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-primary-500">
                  {formatCurrency(project.funding?.amountNeeded, project.funding?.currency)}
                </div>
                <div className="text-sm text-gray-600">Financement recherché</div>
              </div>
              
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-secondary-500">
                  {project.expectedROI ? `${project.expectedROI}%` : 'N/A'}
                </div>
                <div className="text-sm text-gray-600">ROI attendu</div>
              </div>
              
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-text">
                  {project.views}
                </div>
                <div className="text-sm text-gray-600">Vues</div>
              </div>
            </div>
          </div>

          {/* Contenu du projet */}
          <div className="p-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Contenu principal */}
              <div className="lg:col-span-2 space-y-8">
                <div>
                  <h2 className="text-xl font-semibold text-text mb-4">Description</h2>
                  <div className="prose max-w-none">
                    <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                      {project.description}
                    </p>
                  </div>
                </div>

                {project.businessModel && (
                  <div>
                    <h2 className="text-xl font-semibold text-text mb-4">Modèle Économique</h2>
                    <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                      {project.businessModel}
                    </p>
                  </div>
                )}

                {project.targetMarket && (
                  <div>
                    <h2 className="text-xl font-semibold text-text mb-4">Marché Cible</h2>
                    <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                      {project.targetMarket}
                    </p>
                  </div>
                )}

                {project.competitiveAdvantage && (
                  <div>
                    <h2 className="text-xl font-semibold text-text mb-4">Avantage Concurrentiel</h2>
                    <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                      {project.competitiveAdvantage}
                    </p>
                  </div>
                )}
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* Informations sur l'auteur */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-text mb-4">Auteur du Projet</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Nom:</span>
                      <span className="font-medium text-text">{project.author.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Rôle:</span>
                      <span className="font-medium text-text capitalize">{project.author.role}</span>
                    </div>
                    {project.ownerSuivi && (
                      <div className="pt-1"><BadgeEntrepriseSuivie /></div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-600">Pays:</span>
                      <span className="font-medium text-text">{project.location?.country || 'Non spécifié'}</span>
                    </div>
                  </div>
                  
                  {!isAuthor && isAuthenticated && (
                    <button
                      onClick={() => setShowStartConversation(true)}
                      className="w-full mt-4 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
                    >
                      Contacter l'auteur
                    </button>
                  )}
                  
                  {!isAuthor && !isAuthenticated && (
                    <Link
                      to="/login"
                      className="w-full mt-4 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors text-center block"
                    >
                      Se connecter pour contacter
                    </Link>
                  )}
                </div>

                {/* Informations du projet */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-text mb-4">Informations</h3>
                  <div className="space-y-2">
                    {project.teamSize && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Équipe:</span>
                        <span className="font-medium text-text">{project.teamSize}</span>
                      </div>
                    )}
                    {project.timeline && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Timeline:</span>
                        <span className="font-medium text-text">{project.timeline}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-600">Créé le:</span>
                      <span className="font-medium text-text">{formatDate(project.createdAt)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Statut:</span>
                      <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                        {project.status}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-text mb-4">Actions</h3>
                  <div className="space-y-3">
                    {/* Bouton Modifier (seulement pour l'auteur ou admin) */}
                    {(user?.id === project.authorId || user?.role === 'admin') && (
                      <Link
                        to={`/projects/${project.id}/edit`}
                        className="w-full flex items-center justify-center px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
                      >
                        <PencilIcon className="h-4 w-4 mr-2" />
                        Modifier le projet
                      </Link>
                    )}
                    
                    <FavoriteButton projectId={project.id} projectTitle={project.title} />
                    
                    {/* Like et Share en ligne */}
                    <div className="flex space-x-2">
                      <div className="flex-1">
                        <LikeButton 
                          projectId={project.id} 
                          initialLikeCount={project.likeCount || 0}
                          initialIsLiked={false}
                        />
                      </div>
                      <div className="flex-1">
                        <ShareButton project={project} />
                      </div>
                    </div>

                    {isAuthenticated && user?.id !== project.authorId && (
                      <ReportProjectButton projectId={project.id} />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section des commentaires */}
        <CommentsSection projectId={id} />

        {/* Modal de contact */}
        {showContactForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl p-6 w-full max-w-md">
              <h3 className="text-lg font-semibold text-text mb-4">
                Contacter {project.author.name}
              </h3>
              <form onSubmit={handleContactSubmit}>
                <textarea
                  value={contactMessage}
                  onChange={(e) => setContactMessage(e.target.value)}
                  placeholder="Votre message..."
                  rows={4}
                  className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent mb-4"
                  required
                />
                <div className="flex justify-end space-x-2">
                  <button
                    type="button"
                    onClick={() => setShowContactForm(false)}
                    className="px-4 py-2 border border-border text-text rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
                  >
                    Envoyer
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
        
        {/* Modal de conversation */}
        <StartConversation
          project={project}
          isOpen={showStartConversation}
          onClose={() => setShowStartConversation(false)}
        />
      </div>
    </div>
  );
};

export default ProjectDetail;